import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import {
  fetchUserMicrosoftCalendars,
  patchUserMicrosoftCalendarSelection,
} from "../../../api/microsoft";
import { PMButton } from "../../utils/PMButton";
import { MicrosoftEventCalendarSelectorModal } from "./MicrosoftEventCalendarSelectorModal";

/**
 * List/select Microsoft calendars for /me — Promang registers events here.
 * Calendars load from Graph (owned + shared); picker is a searchable modal.
 */
export function UserMicrosoftCalendarSection({ status, onSaved }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [selectorOpen, setSelectorOpen] = useState(false);

  const canUseCalendars = Boolean(status?.connected && status?.can_send_mail);

  const syncSelectionFromStatus = useCallback(() => {
    setSelectedId(status?.event_calendar_id || "");
  }, [status?.event_calendar_id]);

  useEffect(() => {
    syncSelectionFromStatus();
  }, [syncSelectionFromStatus]);

  const loadCalendars = useCallback(() => {
    if (!canUseCalendars) return;
    setLoading(true);
    fetchUserMicrosoftCalendars()
      .then((data) => {
        const list = Array.isArray(data?.calendars) ? data.calendars : [];
        setCalendars(list);
      })
      .catch((e) => {
        const msg =
          e?.response?.data?.errors?.[0] ||
          t("microsoft.user.calendar_load_failed");
        toast.error(msg);
        setCalendars([]);
      })
      .finally(() => setLoading(false));
  }, [canUseCalendars, t]);

  useEffect(() => {
    if (!canUseCalendars) return;
    loadCalendars();
  }, [canUseCalendars, loadCalendars]);

  const selectedCalendar = useMemo(() => {
    if (!selectedId) return null;
    const fromList = calendars.find((c) => c.id === selectedId);
    if (fromList) return fromList;
    if (status?.event_calendar_id === selectedId) {
      return {
        id: selectedId,
        name: status.event_calendar_name || selectedId,
        owner_email: status.event_calendar_owner_email || "",
        can_edit: true,
        owned_by_me: true,
      };
    }
    return { id: selectedId, name: selectedId, owner_email: "", can_edit: true, owned_by_me: true };
  }, [selectedId, calendars, status]);

  const openSelector = useCallback(() => {
    setSelectorOpen(true);
    if (canUseCalendars) loadCalendars();
  }, [canUseCalendars, loadCalendars]);

  const handleModalSelect = useCallback((cal) => {
    setSelectedId(cal?.id || "");
  }, []);

  const handleSave = () => {
    if (!canUseCalendars) return;
    const picked = calendars.find((c) => c.id === selectedId);
    const readOnly =
      picked && picked.can_edit === false;
    if (readOnly) {
      toast.error(t("microsoft.user.calendar_readonly_save"));
      return;
    }
    setSaving(true);
    patchUserMicrosoftCalendarSelection({
      event_calendar_id: selectedId || "",
      event_calendar_name: picked?.name || selectedCalendar?.name || "",
      event_calendar_owner_email:
        picked?.owner_email || selectedCalendar?.owner_email || "",
    })
      .then(() => {
        toast.success(t("microsoft.user.calendar_saved"));
        onSaved?.();
      })
      .catch((e) => {
        const msg =
          e?.response?.data?.errors?.[0] ||
          t("microsoft.user.calendar_save_failed");
        toast.error(msg);
      })
      .finally(() => setSaving(false));
  };

  if (!status?.connected) return null;

  return (
    <div className="mt-6 space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            {t("microsoft.user.calendar_heading")}
          </h3>
          <p className="mt-1 text-xs text-gray-600">
            {t("microsoft.user.calendar_intro")}
          </p>
        </div>
        <button
          type="button"
          disabled={!canUseCalendars || loading}
          onClick={loadCalendars}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          {t("microsoft.user.calendar_refresh")}
        </button>
      </div>

      {!canUseCalendars ? (
        <p className="text-xs text-amber-800">{t("microsoft.user.calendar_need_refresh")}</p>
      ) : null}

      {canUseCalendars ? (
        <>
          <div>
            <span className="block text-xs font-medium text-gray-700">
              {t("microsoft.user.calendar_select_label")}
            </span>
            <button
              type="button"
              onClick={openSelector}
              className="mt-1 flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-left text-sm shadow-sm hover:border-gray-400 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            >
              <span className="min-w-0 flex-1 truncate text-gray-900">
                {selectedCalendar
                  ? `${selectedCalendar.name}${
                      selectedCalendar.owner_email
                        ? ` — ${selectedCalendar.owner_email}`
                        : ""
                    }`
                  : t("microsoft.user.calendar_none")}
              </span>
              <span className="ml-2 flex-shrink-0 text-xs font-medium text-sky-600">
                {t("microsoft.user.calendar_choose")}
              </span>
            </button>
            {selectedCalendar?.can_edit === false ? (
              <p className="mt-1 text-xs text-amber-800">
                {t("microsoft.user.calendar_readonly_hint")}
              </p>
            ) : null}
          </div>
          {status?.event_calendar_name && !calendars.length && !loading ? (
            <p className="text-xs text-gray-500">
              {t("microsoft.user.calendar_current", {
                name: status.event_calendar_name,
                email: status.event_calendar_owner_email
                  ? ` (${status.event_calendar_owner_email})`
                  : "",
              })}
            </p>
          ) : null}
          <div className="flex justify-end pt-1">
            <PMButton
              type="button"
              styleType="primary"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? t("microsoft.status.loading") : t("microsoft.user.calendar_save")}
            </PMButton>
          </div>
        </>
      ) : null}

      <MicrosoftEventCalendarSelectorModal
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        calendars={calendars}
        selectedId={selectedId}
        onSelect={handleModalSelect}
        isLoading={loading}
      />
    </div>
  );
}
